import {
  House,
  Tag,
  Users,
  Newspaper,
  Folder
} from 'lucide-react'
import { renderToStaticMarkup } from 'react-dom/server'
import { Trans } from 'react-i18next'
import { can, route } from '@/utils/index.jsx'

export const buildNavigation = (links, auth, url) => {
  const currentUrl = url.current.startsWith('/', 0) ? url.current : `/${url.current}`

  const navigation = [
    {
      text: <Trans i18nKey='labels.dashboard' />,
      path: '/',
      icon: renderToStaticMarkup(<House size={16} />),
      show: true
    },
    // Administration
    {
      text: <Trans i18nKey='modules.administration' />,
      icon: renderToStaticMarkup(<Folder size={16} />),
      items: [
        // Company
        {
          text: <Trans i18nKey='labels.company' />,
          path: links.admin.administration.company.edit,
          show: can(auth, 'edit_company')
        },
        // Users
        {
          text: <Trans i18nKey='modules.module_groups.users' />,
          items: [
            {
              text: <Trans i18nKey='labels.users' />,
              path: links.admin.administration.users.index,
              show: can(auth, 'view_users'),
              new: {
                path: links.admin.administration.users.create,
                show: can(auth, 'create_users')
              }
            },
            {
              text: <Trans i18nKey='modules.module_groups.roles' />,
              path: links.admin.administration.roles.index,
              show: can(auth, 'view_roles')
            }
          ]
        },
        // Locations
        {
          text: <Trans i18nKey='modules.module_groups.locations' />,
          path: links.admin.administration.locations.index,
          show: can(auth, 'view_locations')
        },
        // Localization
        {
          text: <Trans i18nKey='labels.localization' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.languages' />,
              path: links.admin.administration.languages.index,
              show: can(auth, 'view_languages')
            },
            {
              text: <Trans i18nKey='modules.module_groups.countries' />,
              path: links.admin.administration.countries.index,
              show: can(auth, 'view_countries')
            },
            {
              text: <Trans i18nKey='modules.module_groups.cities' />,
              path: links.admin.administration.cities.index,
              show: can(auth, 'view_cities')
            },
            {
              text: <Trans i18nKey='modules.module_groups.post_codes' />,
              path: links.admin.administration.postCodes.index,
              show: can(auth, 'view_post_codes')
            }
          ]
        },
        // Banks
        {
          text: <Trans i18nKey='labels.banks' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.banks' />,
              path: links.admin.administration.banks.index,
              show: can(auth, 'view_banks')
            },
            {
              text: <Trans i18nKey='modules.module_groups.bank_accounts' />,
              path: links.admin.administration.bankAccounts.index,
              show: can(auth, 'view_bank_accounts')
            }
          ]
        },
        // Currencies
        {
          text: <Trans i18nKey='labels.currencies' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.currencies' />,
              path: links.admin.administration.currencies.index,
              show: can(auth, 'view_currencies')
            }
            // {
            //   text: <Trans i18nKey='labels.currency_exchange_rate' />,
            //   path: links.administration.currencyExchangeRates.index,
            //   show: can(auth, 'view_currencies')
            // }
          ]
        },
        // Unit of measures
        {
          text: <Trans i18nKey='labels.unit_of_measures' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.unit_of_measures' />,
              path: links.admin.administration.unitOfMeasures.index,
              show: can(auth, 'view_unit_of_measures')
            }
            // {
            //   text: <Trans i18nKey='modules.module_groups.measurement_groups' />,
            //   path: links.administration.measurementGroups.index,
            //   show: can(auth, 'view_measurement_groups')
            // }
          ]
        },
        // Payment
        {
          text: <Trans i18nKey='labels.payment_2' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.payment_methods' />,
              path: links.admin.administration.paymentMethods.index,
              show: can(auth, 'view_payment_methods')
            },
            {
              text: <Trans i18nKey='modules.module_groups.payment_terms' />,
              path: links.admin.administration.paymentTerms.index,
              show: can(auth, 'view_payment_terms')
            }
          ]
        },
        // Shipment
        {
          text: <Trans i18nKey='labels.shipment' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.shipment_agents' />,
              path: links.admin.administration.shipmentAgents.index,
              show: can(auth, 'view_shipment_agents')
            },
            {
              text: <Trans i18nKey='modules.module_groups.shipment_methods' />,
              path: links.admin.administration.shipmentMethods.index,
              show: can(auth, 'view_shipment_methods')
            },
            {
              text: <Trans i18nKey='modules.module_groups.transport_methods' />,
              path: links.admin.administration.transportMethods.index,
              show: can(auth, 'view_transport_methods')
            }
          ]
        },
        // VAT Groups
        {
          text: <Trans i18nKey='labels.vat_groups' />,
          items: [
            {
              text: <Trans i18nKey='labels.products' />,
              path: links.admin.administration.vatProductPostingGroups.index,
              show: can(auth, 'view_vat_product_posting_groups')
            },
            {
              text: <Trans i18nKey='labels.accounts' />,
              path: links.admin.administration.vatBusinessPostingGroups.index,
              show: can(auth, 'view_vat_business_posting_groups')
            },
            {
              text: <Trans i18nKey='modules.module_groups.vat_posting_setups' />,
              path: links.admin.administration.vatPostingSetups.index,
              show: can(auth, 'view_vat_posting_setups')
            }
          ]
        },
        // Discount Groups
        {
          text: <Trans i18nKey='labels.discount_groups' />,
          items: [
            {
              text: <Trans i18nKey='labels.products' />,
              path: links.admin.administration.productDiscountGroups.index,
              show: can(auth, 'view_product_discount_groups')
            },
            {
              text: <Trans i18nKey='modules.account' />,
              path: links.admin.administration.accountDiscountGroups.index,
              show: can(auth, 'view_account_discount_groups')
            }
          ]
        },
        // Attributes
        {
          text: <Trans i18nKey='modules.module_groups.attributes' />,
          path: links.admin.administration.attributes.index,
          show: can(auth, 'view_attributes')
        }
      ]
    },
    {
      text: <Trans i18nKey='labels.products' />,
      icon: renderToStaticMarkup(<Tag size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.products' />,
          path: links.admin.products.index,
          show: can(auth, 'view_products'),
          new: {
            path: route(links.admin.products.create, { 'product?': null }),
            show: can(auth, 'create_products')
          }
        },
        {
          text: <Trans i18nKey='labels.product_categories' />,
          path: links.admin.products.categories.index,
          show: can(auth, 'view_product_categories'),
          new: {
            path: links.admin.products.categories.create,
            show: can(auth, 'create_product_categories')
          }
        },
        {
          text: <Trans i18nKey='modules.module_groups.brand' />,
          path: links.admin.products.brands.index,
          show: can(auth, 'view_brands')
        },
        {
          text: <Trans i18nKey='modules.module_groups.manufacturers' />,
          path: links.admin.products.manufacturers.index,
          show: can(auth, 'view_manufacturers')
        }
      ]
    },
    {
      text: <Trans i18nKey='labels.accounts' />,
      icon: renderToStaticMarkup(<Users size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.accounts' />,
          path: links.admin.accounts.index,
          show: can(auth, 'view_accounts'),
          new: {
            path: links.admin.accounts.create,
            show: can(auth, 'create_accounts')
          }
        }
      ]
    },
    {
      text: <Trans i18nKey='labels.cms' />,
      icon: renderToStaticMarkup(<Newspaper size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.pages' />,
          path: links.admin.cms.pages.index,
          show: can(auth, 'view_pages'),
          new: {
            path: route(links.admin.cms.pages.create),
            show: can(auth, 'create_pages')
          }
        },
        {
          text: <Trans i18nKey='labels.posts' />,
          path: links.admin.cms.posts.index,
          show: can(auth, 'view_posts'),
          new: {
            path: links.admin.cms.posts.create,
            show: can(auth, 'create_posts')
          }
        },
        {
          text: <Trans i18nKey='labels.content_categories' />,
          path: links.admin.cms.categories.index,
          show: can(auth, 'view_content_categories'),
          new: {
            path: links.admin.cms.categories.create,
            show: can(auth, 'create_content_categories')
          }
        },
        {
          text: <Trans i18nKey='labels.menus' />,
          path: links.admin.cms.menus.index,
          show: can(auth, 'view_menus'),
          new: {
            path: links.admin.cms.menus.create,
            show: can(auth, 'create_menus')
          }
        },
        {
          text: <Trans i18nKey='labels.testimonials' />,
          path: links.admin.cms.testimonials.index,
          show: can(auth, 'view_testimonials'),
          new: {
            path: route(links.admin.cms.testimonials.create),
            show: can(auth, 'create_testimonials')
          }
        },
        {
          text: <Trans i18nKey='labels.sliders' />,
          path: links.admin.cms.sliders.index,
          show: can(auth, 'view_sliders'),
          new: {
            path: route(links.admin.cms.sliders.create),
            show: can(auth, 'create_sliders')
          }
        },
        {
          text: <Trans i18nKey='labels.highlights' />,
          path: links.admin.cms.highlights.index,
          show: can(auth, 'view_highlights'),
          new: {
            path: route(links.admin.cms.highlights.create),
            show: can(auth, 'create_highlights')
          }
        }
      ]
    }
  ]

  filterNavigation(navigation, currentUrl)

  return navigation
}

const filterNavigation = (navigation, currentUrl) => {
  const toSplice = []

  navigation.forEach((n, i) => {
    if (n.items) {
      filterNavigation(n.items, currentUrl)

      n.items.map(i => {
        i.selected = i.path === currentUrl
        i.expanded = i.path === currentUrl
        return i
      })

      n.show = n.items.filter(i => i.show).length > 0
    }

    if (!n.items) {
      n.selected = n.path === currentUrl
      n.expanded = n.path === currentUrl
    }

    if (!n.show) {
      toSplice.push(i)
    }
  })

  toSplice.reverse().forEach(spliceIndex => {
    navigation.splice(spliceIndex, 1)
  })
}
